'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { getItemKind } from '@/lib/menu/item-kinds';
import { assessmentFromItem } from '@/lib/menu/allergens';
import { availabilityReasonCopy } from '@/lib/menu/availability';
import type { OrderingMenuItem } from '@/lib/menu/ordering/types';
import { formatItemPrice, formatOrderMoney as formatMoney, priceUnitSuffix } from '@/lib/menu/ordering/utils';
import { ItemKindDetails } from '@/components/menu/ItemKindDetails';
import { cn } from '@/components/ui/utils';

/**
 * The browse-mode item sheet.
 *
 * THE THEMING SEAM. This sheet renders in a portal at the page root, on the
 * canvas colour — NOT inside the Place chrome, which inherits the merchant's
 * `--color-*` tokens from the shell root. Its colours therefore have to come
 * from the same source the template's palette came from. The sheet used to
 * paint itself with the platform's static `--pub-*` tokens, so on a near-black
 * restaurant theme it rendered a cream card with black text clapped over the
 * menu: two products on one screen. Everything here derives from the merchant's
 * `palette` via the `--item-*` variables, with the platform tokens kept only as
 * a fallback when no palette reaches the sheet.
 *
 * @see lib/customer/place-chrome-style.ts (the same seam, closed for the shell)
 */

function detailSheetVars(palette?: {
  background?: string; foreground?: string; card?: string; cardForeground?: string;
  border?: string; muted?: string; mutedForeground?: string; accent?: string;
  accentForeground?: string;
}): React.CSSProperties {
  const fg = palette?.cardForeground || palette?.foreground || 'var(--pub-text-primary)';
  const secondary = palette?.mutedForeground || `color-mix(in srgb, ${fg} 72%, transparent)`;
  const muted = palette?.mutedForeground || `color-mix(in srgb, ${fg} 55%, transparent)`;
  const border = palette?.border || 'var(--pub-border-subtle)';
  const accent = palette?.accent || 'var(--pub-ember-fill)';
  const accentFg = palette?.accentForeground || 'var(--color-primary-foreground)';
  const sunken = palette?.muted || 'var(--pub-surface-base)';
  return {
    '--item-surface': palette?.card || 'var(--pub-surface-card)',
    '--item-fg': fg,
    '--item-fg-secondary': secondary,
    '--item-fg-muted': muted,
    '--item-border': border,
    '--item-sunken': sunken,
    '--item-accent': accent,
    '--item-accent-fg': accentFg,
  } as React.CSSProperties;
}

/**
 * The header clamps the item description to two lines; anything longer than this
 * cannot fit in two 12px lines in the sheet's narrow column, so the sheet body
 * repeats it in full only past this point.
 *
 * The threshold is deliberately generous: a mis-estimate that shows a duplicate is
 * a cosmetic bug, whereas one set too low would silently drop the tail of a long
 * description. Export it so the inline sheet in PublicMenuOrderingExperience uses
 * the same number rather than a second, drifting literal.
 */
export const DESCRIPTION_CLAMP_CHARS = 120;

export function MenuItemDetailSheet({
  item,
  open,
  onOpenChange,
  palette,
  surfaceStyle,
  onAdd,
  addLabel = 'Add to order',
  unavailableHint,
}: {
  item: OrderingMenuItem & Record<string, unknown> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd?: () => void;
  addLabel?: string;
  unavailableHint?: string | null;
  palette?: { background?: string; foreground?: string; card?: string; cardForeground?: string; border?: string; muted?: string; mutedForeground?: string; accent?: string };
  surfaceStyle?: React.CSSProperties;
}) {
  const kind = getItemKind(item?.kind);
  const isDish = kind.id === 'dish';
  const kindLabel = isDish ? null : kind.label;

  const available_Days = item && 'available_Days' in item ? (item as any).available_Days as string[] | undefined : undefined;
  const available_from = item && 'available_from' in item ? (item as any).available_from as string | undefined : undefined;
  const available_until = item && 'available_until' in item ? (item as any).available_until as string | undefined : undefined;
  const is_coming_soon = item && 'is_coming_soon' in item ? Boolean((item as any).is_coming_soon) : false;
  const is_alcoholic = item && 'is_alcoholic' in item ? Boolean((item as any).is_alcoholic) : false;

  const hasOptionGroups = Boolean(item?.option_groups && item.option_groups.length > 0);
  const requiredGroups = hasOptionGroups ? item!.option_groups!.filter((g) => g.required).length : 0;

  /**
   * Double-tap guard on the pinned CTA. `onAdd` is synchronous parent state
   * (close this sheet, open the composer), so a second tap landing before the
   * close commits would fire it twice. 400ms swallows a double-tap while still
   * letting a guest who dismissed a validation toast immediately try again.
   */
  const lastAddAtRef = useRef(0);
  const openerRef = useRef<HTMLElement | null>(null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        onOpenAutoFocus={() => {
          const document = globalThis.document;
          const activeElement = document.activeElement;
          const HTMLElementConstructor = document.defaultView?.HTMLElement;
          openerRef.current =
            HTMLElementConstructor && activeElement instanceof HTMLElementConstructor && activeElement !== document.body
              ? activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const opener = openerRef.current;
          openerRef.current = null;
          if (opener?.isConnected) opener.focus();
        }}
        className={cn(
          'max-h-[85svh] w-full !max-w-none gap-0 overflow-hidden rounded-t-3xl border-x-0 border-b-0 p-0',
          'text-[var(--item-fg)] shadow-[0_-18px_54px_rgba(0,0,0,0.22)]',
          'md:!left-1/2 md:!right-auto md:w-[min(94vw,42rem)] md:-translate-x-1/2',
        )}
        style={{
          ...surfaceStyle,
          ...detailSheetVars(palette),
          backgroundColor: 'var(--item-surface)',
          color: 'var(--item-fg)',
        }}
        overlayStyle={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      >
        {item && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {/* Drag handle */}
            <div className="flex shrink-0 justify-center pt-2.5 pb-1">
              <div className="h-1 w-8 rounded-full" style={{ backgroundColor: 'rgba(128,128,128,0.4)' }} />
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:opacity-80"
              style={{ backgroundColor: 'rgba(128,128,128,0.16)', color: 'var(--item-fg-secondary)' }}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header: compact thumbnail left, title + description to the side,
                price prominent on the right. The thumbnail is deliberately small —
                the description is the star, so the column gives it the room. */}
            <div className="flex items-start gap-3.5 px-5 pt-2 pb-3">
              {item.image ? (
                <div
                  className="relative h-[56px] w-[56px] shrink-0 overflow-hidden rounded-lg"
                  style={{ border: '1px solid var(--item-border)' }}
                >
                  <Image
                    src={item.image}
                    alt={item.title || item.name || 'Menu item'}
                    fill
                    unoptimized
                    className="object-cover"
                    loading="lazy"
                  />
                  {!item.available_for_order && item.available_for_order !== undefined && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="rounded-full bg-black/70 px-1.5 py-0.5 text-[9px] font-medium text-white">
                        {item.availability_label || 'Unavailable'}
                      </span>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <SheetHeader className="p-0 text-left">
                    <SheetTitle className="text-lg font-bold leading-tight" style={{ color: 'var(--item-fg)' }}>
                      {item.title || item.name || 'Menu item'}
                    </SheetTitle>
                  </SheetHeader>

                  {(item.price !== undefined || item.price_value !== undefined) && (
                    <span
                      className="flex h-9 shrink-0 items-center gap-1 rounded-full px-3 text-sm font-extrabold tabular-nums shadow-sm"
                      style={{ backgroundColor: 'var(--item-sunken)', color: 'var(--item-fg)' }}
                    >
                      {formatItemPrice(item)}
                      {priceUnitSuffix(item) && (
                        <span className="text-[9px] font-normal" style={{ color: 'var(--item-fg-muted)' }}>{priceUnitSuffix(item)}</span>
                      )}
                    </span>
                  )}
                </div>

                {/* Description snippet beside the title — the mid-line detail the
                    guest is looking for before deciding to open the item. */}
                {item.description?.trim() && (
                  <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug" style={{ color: 'var(--item-fg-secondary)' }}>
                    {item.description.trim()}
                  </p>
                )}

                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {kindLabel && (
                    <span
                      className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{ backgroundColor: 'var(--item-sunken)', color: 'var(--item-fg-muted)' }}
                    >
                      {kindLabel}
                    </span>
                  )}
                  {hasOptionGroups && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{ backgroundColor: 'var(--item-accent)', color: 'var(--item-accent-fg)' }}
                    >
                      {requiredGroups > 0 ? (
                        <>{requiredGroups} required option{requiredGroups === 1 ? '' : 's'}</>
                      ) : (
                        'Customisable'
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="h-px" style={{ backgroundColor: 'var(--item-border)' }} />

            {/* Scrollable detail content */}
            <div className="flex flex-col gap-3 px-5 pb-4 pt-3">
              {/* Full description.
                  The header above already shows this text, clamped to two lines. For
                  every short description — which is nearly all of them — that clamp
                  hides nothing, so rendering it again just printed the same sentence
                  twice in the same dialog. Only repeat it when the clamp is actually
                  losing content.

                  The threshold is deliberately generous: a mis-estimate that shows a
                  duplicate is a cosmetic bug, whereas one that is too low would
                  silently drop the tail of a long description. 120 characters cannot
                  fit in two 12px lines in this column, so anything shorter is
                  certainly intact above. */}
              {/* Trimmed once, into a local. `item.description?.trim() &&
                  item.description.trim()` reads as if the optional chain narrows the
                  field, but it does not: narrowing does not survive a second property
                  access on a mutable object, so the compiler still sees the field as
                  possibly undefined. It also called `.trim()` twice per render for no
                  reason. */}
              {(() => {
                const description = item.description?.trim() ?? '';
                if (description.length <= DESCRIPTION_CLAMP_CHARS) return null;
                return (
                  <p className="text-[13px] leading-relaxed" style={{ color: 'var(--item-fg-secondary)' }}>
                    {description}
                  </p>
                );
              })()}

              {/* Tags */}
              {item.tags?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full px-2.5 py-0.5 text-[10px] font-medium"
                      style={{ backgroundColor: 'var(--item-sunken)', color: 'var(--item-fg-muted)' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}

              {/* Dietary tags + allergens */}
              {(() => {
                const assessment = assessmentFromItem(item);
                const derivedUnconfirmed = item.unconfirmed_derived_allergens ?? [];
                return (
                  <div className="flex flex-wrap gap-1.5">
                    {assessment === 'not_assessed' && (
                      <p className="w-full text-xs font-medium" style={{ color: 'var(--color-warning)' }}>
                        Allergen information not available for this item — please ask staff.
                      </p>
                    )}
                    {(item.dietary_tags || []).map((tag) => (
                      <span
                        key={`diet-${tag}`}
                        className="rounded-full px-2.5 py-0.5 text-[10px] font-medium"
                        style={{ backgroundColor: 'var(--color-success-soft)', color: 'var(--color-success)' }}
                      >
                        {tag}
                      </span>
                    ))}
                    {assessment === 'declared' && item.allergens?.length ? (
                      <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: 'var(--color-warning-soft)', color: 'var(--color-warning)' }}>
                        Contains: {item.allergens.join(', ')}
                      </span>
                    ) : assessment === 'none_present' ? (
                      <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: 'var(--color-success-soft)', color: 'var(--color-success)' }}>
                        No known allergens
                      </span>
                    ) : null}
                    {derivedUnconfirmed.length > 0 && (
                      <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: 'var(--color-warning-soft)', color: 'var(--color-warning)' }}>
                        May contain: {derivedUnconfirmed.join(', ')} (unconfirmed)
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Availability verdict */}
              {(() => {
                const copy = availabilityReasonCopy(item.availability_verdict ?? { available: true });
                if (!copy) return null;
                return (
                  <p className="text-xs font-medium" style={{ color: 'var(--color-warning)' }}>{copy}</p>
                );
              })()}

              {/* Kind-specific details */}
              {!isDish && item.kind && (
                <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--item-sunken)' }}>
                  <ItemKindDetails item={item} />
                </div>
              )}

              {/* Option groups — the "before you commit, trim the plate to your
                  taste" block. Each group lists its choices and whether it is
                  mandatory, so the guest knows what the cook will ask them. */}
              {hasOptionGroups && (
                <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--item-sunken)' }}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--item-fg-muted)' }}>
                      Make it yours
                    </p>
                    <span className="text-[10px] font-medium" style={{ color: 'var(--item-fg-muted)' }}>
                      Customisation available
                    </span>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {item.option_groups!.map((group) => (
                      <div
                        key={group.id}
                        className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-2"
                        style={{ backgroundColor: 'var(--item-surface)', border: '1px solid var(--item-border)' }}
                      >
                        <span className="truncate text-[12px] font-medium" style={{ color: 'var(--item-fg)' }}>
                          {group.name}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                            group.required ? 'text-[var(--item-accent)]' : '',
                          )}
                          style={group.required
                            ? { backgroundColor: `color-mix(in srgb, var(--item-accent) 14%, transparent)`, color: 'var(--item-accent)' }
                            : { backgroundColor: 'rgba(128,128,128,0.14)', color: 'var(--item-fg-muted)' }}
                        >
                          {group.required
                          ? `Required${group.options?.some((o) => o.is_default) ? ' · preset' : ''}`
                          : 'Optional'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Weight pricing info */}
              {item.pricing_mode === 'per_unit_weight' && (
                <div className="rounded-xl p-3 text-sm" style={{ backgroundColor: 'var(--item-sunken)' }}>
                  <div className="flex items-center justify-between">
                    <span style={{ color: 'var(--item-fg-secondary)' }}>Priced by weight</span>
                    <span className="font-medium tabular-nums" style={{ color: 'var(--item-fg)' }}>
                      {item.price_per_unit_weight ? formatMoney(item.price_per_unit_weight, item.currency) : '-'} / {item.weight_unit || 'kg'}
                    </span>
                  </div>
                  {(item.min_weight || item.max_weight) && (
                    <p className="mt-1 text-[11px]" style={{ color: 'var(--item-fg-muted)' }}>
                      {item.min_weight || 0}–{item.max_weight || '∞'} {item.weight_unit || 'kg'}
                    </p>
                  )}
                </div>
              )}

              {/* Availability schedule */}
              {(available_Days?.length || available_from || available_until) && (
                <div className="rounded-xl p-3 text-sm" style={{ backgroundColor: 'var(--item-sunken)' }}>
                  <p className="font-medium" style={{ color: 'var(--item-fg)' }}>Availability</p>
                  {available_Days?.length ? (
                    <p className="mt-1 text-[13px]" style={{ color: 'var(--item-fg-secondary)' }}>
                      Days: {available_Days.join(', ')}
                    </p>
                  ) : null}
                  {available_from || available_until ? (
                    <p className="mt-0.5 text-[13px]" style={{ color: 'var(--item-fg-secondary)' }}>
                      Time: {available_from || '—'} – {available_until || '—'}
                    </p>
                  ) : null}
                </div>
              )}

              {/* Coming soon badge */}
              {is_coming_soon && (
                <div className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: 'var(--color-info-soft)', color: 'var(--color-info)' }}>
                  Coming soon
                </div>
              )}

              {/* Alcoholic notice */}
              {is_alcoholic && (
                <div className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: 'var(--color-warning-soft)', color: 'var(--color-warning)' }}>
                  Contains alcohol
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pinned CTA — accent-coloured so it reads as the one action, and the
            label tells the guest whether there is decision-making ahead. */}
        {item && (onAdd || unavailableHint) ? (
          <div
            className="shrink-0 border-t px-5 py-4"
            style={{
              borderColor: 'var(--item-border)',
              paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
            }}
          >
            {onAdd ? (
              <button
                type="button"
                onClick={() => {
                  const now = Date.now();
                  if (now - lastAddAtRef.current < 400) return;
                  lastAddAtRef.current = now;
                  onAdd();
                }}
                className="flex h-12 w-full items-center justify-between rounded-2xl px-5 text-base font-bold transition-all duration-150 hover:opacity-90 active:scale-[0.98]"
                style={{
                  backgroundColor: 'var(--item-accent)',
                  color: 'var(--item-accent-fg)',
                }}
              >
                <span>{hasOptionGroups ? 'Customize & add' : addLabel}</span>
                <span className="tabular-nums">{formatItemPrice(item)}</span>
              </button>
            ) : (
              <p className="text-center text-xs leading-4" style={{ color: 'var(--item-fg-secondary)' }}>
                {unavailableHint}
              </p>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
